import { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { CourseContentService } from "../../../service/UserService/CourseContentService";
import usePagination from "../../../assets/hooks/PaginationHooks/usePagination";

export const useCourseContent = (courseId) => {
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showContent, setShowContent] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [courseContent, setCourseContent] = useState([]);
  const [courseTitle, setCourseTitle] = useState("");
  const [quizAvailable, setQuizAvailable] = useState(false);
  const [quizAttempted, setQuizAttempted] = useState(false);
  const [totalAttempts, setTotalAttempts] = useState(0);

  const {
    currentPage,
    pageSize,
    totalElements,
    setTotalElements,
    handleTableChange,
    setCurrentPage,
  } = usePagination(1, 10, 0);

  useEffect(() => {
    const token = localStorage.getItem("authtoken");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded?.userId) {
          setUserId(decoded.userId);
          localStorage.setItem("userId", decoded.userId);
        }
      } catch (err) {
        console.error("Error decoding JWT:", err);
      }
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(search);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadCourseContent = async (page = currentPage, size = pageSize, text = searchTerm) => {
    if (!userId || !courseId) return;
    
    setLoading(true);
    try {
      const data = await CourseContentService.getCourseContent(courseId, page, size, text);
      
      if (data?.courseTitle) setCourseTitle(data.courseTitle);
      if (data?.quizAvailable !== undefined) setQuizAvailable(data.quizAvailable);
      if (data?.quizAttempted !== undefined) setQuizAttempted(data.quizAttempted);

      setTotalElements(data?.items?.totalElements ?? 0);

      const contentItems = (data?.items?.content ?? [])
        .filter((item) => item.itemType === "CONTENT")
        .map((item) => ({
          contentId: item.itemId,
          title: item.title,
          description: item.description,
          resourceLink: item.resourceLink,
          completionPercentage: parseFloat((item.completionPercentage ?? 0).toFixed(2)),
          minCompletionPercentage: item.minCompletionPercentage,
          acknowledged: item.acknowledged,
        }));

      setCourseContent(contentItems);
    } catch (error) {
      setErrorMessage("Failed to fetch course content.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadCourseContent(currentPage, pageSize, searchTerm);
  }, [userId, courseId, currentPage, pageSize, searchTerm]);

  const allAcknowledged = courseContent.length > 0 && courseContent.every((item) => item.acknowledged === true);


  const refreshCourseProgress = () => loadCourseContent(currentPage, pageSize, searchTerm);

  return {
    userId,
    loading,
    errorMessage,
    showContent,
    setShowContent,
    selectedRecord,
    setSelectedRecord,
    search,
    setSearch,
    currentPage,
    pageSize,
    totalElements,
    courseContent,
    courseTitle,
    quizAvailable,
    quizAttempted,
    totalAttempts,
    allAcknowledged,
    handleTableChange,
    refreshCourseProgress,
    setCourseContent,
  };
};